import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { 
  Box,
  Grid,
  Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import KpiCard from '../TableauObs/common/KpiCard';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import {
    fetchUpcomingRenewals,
    fetchRenewalsTableData,
    fetchRenewalsFilterOptions,
    exportRenewalsData
} from '../../service/service';

interface ProductCatalogRenewalsProps {
    filters?: {
        filterOption?: string;
        startDate?: string;
        endDate?: string;
        filterList?: null | { [key: string]: any };
    };
    checkedListRef?: React.MutableRefObject<any>;
    resetFiltersRef?: React.MutableRefObject<(() => void) | null>;
}

type FormatterProps = {
    row: any;
    onPublisherClick?: (row: any) => void;
};

const PublisherFormatter: React.FC<FormatterProps> = ({ row, onPublisherClick }) => {
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onPublisherClick) {
            onPublisherClick(row);
        }
    };

    return (
        <MuiLink
            component="button"
            onClick={handleClick}
            sx={{
                cursor: 'pointer',
                color: '#000',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                    textDecoration: 'underline',
                    color: '#1976d2'
                },
                textAlign: 'left',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                background: 'none',
                border: 'none',
                padding: 0
            }}
        >
            {row.publisher || '-'}
        </MuiLink>
    );
};

const ProductNameFormatter: React.FC<FormatterProps> = ({ row }) => (
    <RowTypography>{row.productName || '-'}</RowTypography>
);

const PurchaseTypeFormatter: React.FC<FormatterProps> = ({ row }) => (
    <RowTypography>{row.purchaseType || '-'}</RowTypography>
);

const VendorFormatter: React.FC<FormatterProps> = ({ row }) => (
    <RowTypography>{row.vendor || '-'}</RowTypography>
);

const VendorContactFormatter: React.FC<FormatterProps> = ({ row }) => (
    <RowTypography>{row.vendorContact || '-'}</RowTypography>
);

const CNDAFormatter: React.FC<FormatterProps> = ({ row }) => (
    <RowTypography>{row.cnda || '-'}</RowTypography>
);

const MSAFormatter: React.FC<FormatterProps> = ({ row }) => (
    <RowTypography>{row.msa || '-'}</RowTypography>
);



 const renewalStatusColors = {
 
  'completed': { color: '#01C008', bg: 'rgba(1, 192, 8, 0.15)' },
  'delayed': { color: '#FF4444', bg: 'rgba(255, 68, 68, 0.15)' },
  'Not yet tracked': { color: '#FFA500', bg: 'rgba(255, 165, 0, 0.15)' },
   'pending': { color: '#ca8a04', bg: '#fef9c3' },
};

interface Renewal {
    _id: string;
    publisher: string;
    productName: string;
    vendor: string;
    vendorContact: string;
    cnda: string;
    msa: string;
    dynamicRenewalStatus: 'delayed' | 'completed' | 'pending' | 'Not yet tracked';
}

const RenewalStatusFormatter: React.FC<FormatterProps> = ({ row }: { row: Renewal }) => {
  const config =
    renewalStatusColors[row.dynamicRenewalStatus] ||
    renewalStatusColors['delayed'];

  return (
    <RowTypography
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.5,            
        py: 0.5,            
        backgroundColor: '#000',
        color: config.color,
        fontSize: '14px',
        fontWeight: 600,
        borderRadius: '12px',
        lineHeight: 1,      
        whiteSpace: 'nowrap'
      }}
    >
      {row.dynamicRenewalStatus?.toUpperCase()}
    </RowTypography>
  );
};

interface PublisherColumnFormatterProps {
    row: any;
    onPublisherClick?: (row: any) => void;
}

const PublisherColumnFormatter: React.FC<PublisherColumnFormatterProps> = ({ row, onPublisherClick }) => {
    return <PublisherFormatter row={row} onPublisherClick={onPublisherClick} />;
};

const KPI_TYPE_MAP: Record<string, string> = {
    delayed: 'Delayed Renewals',
    upcoming30: 'Next 30 Days',
    upcoming60: 'Next 60 Days',
    upcoming90: 'Next 90 Days',
    notApplicable: 'Missing Renewal Date',
    all: 'All Renewals'
};

const KPI_CATEGORY_MAP: Record<string, string> = {
    'Delayed Renewals': 'delayed',
    'Next 30 Days': 'upcoming30',
    'Next 60 Days': 'upcoming60',
    'Next 90 Days': 'upcoming90',
    'Missing Renewal Date': 'notApplicable',
    'All Renewals': 'all'
};

const ProductCatalogRenewals: React.FC<ProductCatalogRenewalsProps> = ({ checkedListRef, filters: externalFilters, resetFiltersRef }) => {
    const navigate = useNavigate();
    const [selectedTitle, setSelectedTitle] = useState<string>(KPI_TYPE_MAP['delayed']);
    const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
    const [searchValues, setSearchValues] = useState<Record<string, string>>({});
    
    const [loadingState, setLoadingState] = useState({
        renewalMetrics: false,
        tableData: false,
        filterOptions: false
    });

    const [renewalMetricsData, setRenewalMetricsData] = useState({
        total: 0,
        upcomingRenewals: {
            "30_days": { count: 0, percentage: 0 },
            "60_days": { count: 0, percentage: 0 },
            "90_days": { count: 0, percentage: 0 },
            "delayedRenewals": { count: 0, percentage: 0 },
            notApplicable: { count: 0, percentage: 0 }
        },
        daterange: {
            today: new Date(),
            days30: new Date(),
            days60: new Date(),
            days90: new Date()
        }
    });

    const [tableData, setTableData] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: 50
    });

    const [filterDataOptions, setFilterDataOptions] = useState<Record<string, string[]>>({
        publisher: [],
        productName: [],
        purchaseType: [],
        vendor: [],
        renewalStatus: [],
        vendorContact: [],
        applicationOwner: [],
        managedBy: []
    });

    const setLoading = (key: keyof typeof loadingState, value: boolean) => {
        setLoadingState(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handlePublisherClick = useCallback((row: any) => {
        navigate(`/productCatalog/renewals/${row._id}`, {
            state: {
                ...row,
                from: 'renewals'
            }
        });
    }, [navigate]);

    const kpiConfigs = useMemo(() => [
        {
            id: "delayed",
            title: "Delayed Renewals",
            tooltip: "Products with past due renewal dates",
            number: renewalMetricsData.upcomingRenewals.delayedRenewals.count, 
            isLoading: loadingState.renewalMetrics
        },
        {
            id: "upcoming30",
            title: "Next 30 Days",
            tooltip: "Renewals due within 30 days",
            number: renewalMetricsData.upcomingRenewals["30_days"].count || 0,
            isLoading: loadingState.renewalMetrics
        },
        {
            id: "upcoming60",
            title: "Next 60 Days",
            tooltip: "Renewals due in 31-60 days",
            number: renewalMetricsData.upcomingRenewals["60_days"].count || 0,
            isLoading: loadingState.renewalMetrics
        },
        {
            id: "upcoming90",
            title: "Next 90 Days",
            tooltip: "Renewals due in 61-90 days",
            number: renewalMetricsData.upcomingRenewals["90_days"].count || 0,
            isLoading: loadingState.renewalMetrics
        }
    ], [
        renewalMetricsData,
        loadingState.renewalMetrics
    ]);

    const fetchUpcomingRenewalsData = useCallback(async () => {
        setLoading('renewalMetrics', true);
        try {
            const response = await fetchUpcomingRenewals();
            
            if (response?.success && response?.data) {
                const data = response.data;
                
                setRenewalMetricsData({
                    ...data,
                    upcomingRenewals: {
                        ...data.upcomingRenewals,
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching upcoming renewals:', error);
        } finally {
            setLoading('renewalMetrics', false);
        }
    }, []);

    const fetchTableData = useCallback(async (page = 1, pageSize = 50) => {
        setLoading('tableData', true);
        try {
            const renewalCategory = KPI_CATEGORY_MAP[selectedTitle] || 'all';
            const payload = {
                page,
                pageSize,
                sortBy: 'renewalDate',
                orderBy: 'ASC',
                renewalCategory,
                filters: currentFilters,
                search: searchValues
            };
            const response = await fetchRenewalsTableData(payload);
            if (response?.renewals?.length > 0) {
                console.log("Successfully fetched renewals data");
                setTableData(response.renewals || []);
                setPagination(response.pagination || {
                    currentPage: page,
                    totalPages: 0,
                    totalElements: 0,
                    pageSize: pageSize
                });
            } else {
                setTableData([]);
                setPagination({
                    currentPage: 1,
                    totalPages: 0,
                    totalElements: 0,
                    pageSize: pageSize
                });
            }
        } catch (error) {
            console.error('Error fetching renewals table data:', error);
            setTableData([]);
            setPagination({
                currentPage: 1,
                totalPages: 0,
                totalElements: 0,
                pageSize: pagination.pageSize
            });
        } finally {
            setLoading('tableData', false);
        }
    }, [selectedTitle, currentFilters, searchValues, pagination.pageSize]);

    const fetchFilterOptions = useCallback(async () => {
        setLoading('filterOptions', true);
        try {
            const fields = ['publisher', 'productName', 'purchaseType', 'vendor', 'renewalStatus', 'vendorContact', 'applicationOwner', 'managedBy'];
            
            const optionsPromises = fields.map(field => 
                fetchRenewalsFilterOptions(field)
                    .then(data => ({ field, data }))
                    .catch(error => {
                        console.error(`Error fetching ${field} options:`, error);
                        return { field, data: [] };
                    })
            );

            const results = await Promise.all(optionsPromises);
            
            const optionsMap: Record<string, string[]> = {};
            results.forEach(result => {
                optionsMap[result.field] = result.data;
            });

            setFilterDataOptions(optionsMap);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        } finally {
            setLoading('filterOptions', false);
        }
    }, []);

    const handleKPICardClick = useCallback((cardTitle: string) => {
        const newSelectedTitle = selectedTitle === cardTitle ? KPI_TYPE_MAP['all'] : cardTitle;
        setSelectedTitle(newSelectedTitle);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, [selectedTitle]);

    const handleFilterChange = useCallback((filter: { [key: string]: any }) => {
        const filterListMap: { [key: string]: any } = {};
        Object.keys(filter).forEach((key) => {
            const value = filter[key];
            if (value != null && (!Array.isArray(value) || value.length > 0)) {
                filterListMap[key] = value;
            }
        });

        setCurrentFilters(filterListMap);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, []);

    const handleSearch = useCallback((searchDetails: any, columnKey: any) => {
        setSearchValues(prev => ({
            ...prev,
            [columnKey]: searchDetails
        }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, []);

    const handleSorting = useCallback((sorting: any) => {
        console.log('Sorting requested:', sorting);
    }, []);

    const handlePageChange = useCallback((event: any, value: any) => {
        setPagination(prev => ({ ...prev, currentPage: value }));
        fetchTableData(value, pagination.pageSize);
    }, [fetchTableData, pagination.pageSize]);

    const handlePageSizeChange = useCallback((event: any) => {
        const newSize = Number.parseInt(event.target.value);
        setPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
        fetchTableData(1, newSize);
    }, [fetchTableData]);

    const publisherFormatter = useCallback((props: any) => (
        <PublisherColumnFormatter 
            row={props.row} 
            onPublisherClick={handlePublisherClick} 
        />
    ), [handlePublisherClick]);

    const renewalsColumns = useMemo(() => [
        {
            key: 'publisher',
            name: 'Publisher',
            minWidth: 180,
            formatter: publisherFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'productName',
            name: 'Product Name',
            minWidth: 200,
            formatter: ProductNameFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'purchaseType',
            name: 'Purchase Type',
            minWidth: 150,
            formatter: PurchaseTypeFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'vendor',
            name: 'Vendor',
            minWidth: 180,
            formatter: VendorFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'vendorContact',
            name: 'Vendor Contact',
            minWidth: 180,
            formatter: VendorContactFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'cnda',
            name: 'CNDA',
            minWidth: 150,
            formatter: CNDAFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'msa',
            name: 'MSA',
            minWidth: 150,
            formatter: MSAFormatter,
            resizable: true,
            flexGrow: 1
        },
        {
            key: 'renewalStatus',
            name: 'Renewal Status',
            minWidth: 180,
            formatter: RenewalStatusFormatter,
            resizable: true,
            flexGrow: 1
        }
    ], [publisherFormatter]);

    const defaultColumns = [
        'Publisher',
        'Product Name',
        'Purchase Type',
        'Vendor',
        'Vendor Contact',
        'CNDA',
        'MSA',
        'Renewal Status'
    ];

    const handleExport = useCallback(async () => {
        try {
            const renewalCategory = KPI_CATEGORY_MAP[selectedTitle] || 'all';
            const exportData = {
                formatType: "EXCEL",
                renewalCategory,
                filters: currentFilters,
                search: searchValues,
                sortBy: 'renewalDate',
                orderBy: 'ASC',
                exportColumns: defaultColumns
            };
            await exportRenewalsData(exportData);
        } catch (error) {
            console.error('Error exporting renewals data:', error);
        }
    }, [selectedTitle, currentFilters, searchValues, defaultColumns]);

    useEffect(() => {
        fetchUpcomingRenewalsData();
        fetchFilterOptions();
    }, [fetchUpcomingRenewalsData, fetchFilterOptions]);

    useEffect(() => {
        fetchTableData(pagination.currentPage, pagination.pageSize);
    }, [fetchTableData, pagination.currentPage, pagination.pageSize]);

    useEffect(() => {
        if (resetFiltersRef) {
            resetFiltersRef.current = () => {
                setSelectedTitle(KPI_TYPE_MAP['delayed']);
                setCurrentFilters({});
                setSearchValues({});
                setPagination(prev => ({ ...prev, currentPage: 1 }));
            };
        }
        
        return () => {
            if (resetFiltersRef) {
                resetFiltersRef.current = null;
            }
        };
    }, [resetFiltersRef]);

    useEffect(() => {
        const delayedCount = renewalMetricsData.total - 
            (renewalMetricsData.upcomingRenewals["30_days"].count + 
             renewalMetricsData.upcomingRenewals["60_days"].count + 
             renewalMetricsData.upcomingRenewals["90_days"].count + 
             renewalMetricsData.upcomingRenewals.notApplicable.count);
        
        const updatedConfigs = kpiConfigs.map(config => {
            if (config.id === 'delayed') {
                return { ...config, number: delayedCount > 0 ? delayedCount : 0 };
            }
            return config;
        });
        
    }, [renewalMetricsData, kpiConfigs]);

    const leftKPIs = kpiConfigs.slice(0, 2);
    const rightKPIs = kpiConfigs.slice(2);

    return (
        <PageWrapper sx={{ paddingTop: '40px !important' }}>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                        {leftKPIs.map(({ id, title, tooltip, number, isLoading }) => (
                            <Grid item xs={12} sm={6} key={id}>
                                <KpiCard
                                    isLoading={isLoading}
                                    title={title}
                                    hasTooltip
                                    tooltipLabel={tooltip}
                                    number={Number(number) || 0}
                                    isCost={false}
                                    theme="light"
                                    containerProps={{
                                        sx: { 
                                            minHeight: "140px", 
                                            width: "100%",
                                            cursor: 'pointer'
                                        },
                                        onClick: () => handleKPICardClick(title)
                                    }}
                                    isSelected={selectedTitle === title}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                        {rightKPIs.map(({ id, title, tooltip, number, isLoading }) => (
                            <Grid item xs={12} sm={6} key={id}>
                                <KpiCard
                                    isLoading={isLoading}
                                    title={title}
                                    hasTooltip
                                    tooltipLabel={tooltip}
                                    number={Number(number) || 0}
                                    isCost={false}
                                    theme="light"
                                    containerProps={{
                                        sx: { 
                                            minHeight: "140px", 
                                            width: "100%",
                                            cursor: 'pointer'
                                        },
                                        onClick: () => handleKPICardClick(title)
                                    }}
                                    isSelected={selectedTitle === title}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
                <CustomizedDataGrid
                    title={`Renewals List - ${selectedTitle}`}
                    tableColumns={renewalsColumns}
                    tableData={tableData}
                    tableType="renewals-list"
                    defaultColumnNames={defaultColumns}
                    onSorting={handleSorting}
                    currSorting={{
                        sortBy: 'renewalDate',
                        sortDir: 'ASC'
                    }}
                    filterDataOptions={filterDataOptions}
                    searchDataValues={searchValues}
                    onFilterCheck={handleFilterChange}
                    currFilter={currentFilters}
                    onFilterSearch={handleSearch}
                    onHeaderSelectAll={() => { }}
                    onFilterClick={() => { }}
                    showExport={true}
                    fileName={`renewals-${selectedTitle.toLowerCase().replace(/ /g, '-')}-report`}
                    tableName="renewals-list"
                    formatType="EXCEL"
                    exportColumns={defaultColumns}
                    exportTableData={handleExport}
                    page={pagination.currentPage}
                    pageSize={pagination.pageSize}
                    paginationData={pagination}
                    listLength={tableData.length}
                    handlepageSizeChange={handlePageSizeChange}
                    handlePageChange={handlePageChange}
                    showGoToPage={true}
                    isFetching={loadingState.tableData}
                    isDateRangeFilter={false}
                    isDisabled={false}
                    pageSizeOptions={[25, 50, 100, 200]}
                />
            </Box>
        </PageWrapper>
    );
};

export default ProductCatalogRenewals;
